import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { playerAPI } from '../services/api';
import { publicAssetUrl } from '../utils/publicAssetUrl';
import FormattedProductDescription, {
  productDescriptionPlainLength,
} from '../utils/FormattedProductDescription';
import PlayerInviteSearch from './playerInvites/PlayerInviteSearch';

const Box = styled.div`
  margin: 1.25rem 0;
  padding: 1.15rem 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: #374151;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  background: #fff;
  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const Help = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0 0 0.75rem;
  line-height: 1.45;
`;

const Button = styled.button`
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  border: none;
  background: ${(p) => (p.$variant === 'danger' ? '#fff' : p.$variant === 'secondary' ? '#fff' : '#043663')};
  color: ${(p) => (p.$variant === 'danger' ? '#b91c1c' : p.$variant === 'secondary' ? '#043663' : 'white')};
  border: ${(p) =>
    p.$variant === 'danger'
      ? '1px solid #fecaca'
      : p.$variant === 'secondary'
        ? '1px solid #cbd5e1'
        : 'none'};
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  &:disabled {
    opacity: 0.55;
  }
`;

const SearchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: stretch;
  ${Input} {
    margin-bottom: 0;
    flex: 1 1 180px;
  }
`;

const Msg = styled.div`
  margin-top: 0.65rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  background: ${(p) => (p.$ok ? '#d1fae5' : p.$warn ? '#fef3c7' : '#fee2e2')};
  color: ${(p) => (p.$ok ? '#065f46' : p.$warn ? '#92400e' : '#991b1b')};
`;

const PartnerCard = styled.div`
  margin-top: 0.85rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: center;
  padding: 0.85rem 0.95rem;
  border-radius: 12px;
  background: #fff;
  border: 1px solid ${(p) => (p.$confirmed ? '#86efac' : '#e2e8f0')};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #043663;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.15rem;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PartnerMeta = styled.div`
  flex: 1 1 160px;
  min-width: 0;
`;

const PartnerName = styled.div`
  font-weight: 700;
  color: #0f172a;
  font-size: 1.02rem;
  line-height: 1.3;
`;

const PartnerSub = styled.div`
  margin-top: 0.2rem;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.4;
`;

const SkillPill = styled.span`
  display: inline-block;
  margin-top: 0.4rem;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-left: auto;
`;

const ConfirmedBadge = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #15803d;
  margin-bottom: 0.25rem;
`;

/* —— Package catalog —— */
const SectionHead = styled.div`
  margin-bottom: 0.85rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
`;

const ProductRail = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols || 2}, minmax(0, 1fr));
  gap: 0.85rem;
  @media (max-width: 900px) {
    grid-template-columns: repeat(${(p) => Math.min(p.$cols || 2, 2)}, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 0.75rem;
    padding-bottom: 0.35rem;
    margin: 0 -0.15rem;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
`;

const ProductTile = styled.article`
  background: #fff;
  border: 2px solid ${(p) => (p.$active ? '#D52B1E' : '#e2e8f0')};
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${(p) =>
    p.$active ? '0 0 0 1px rgba(213, 43, 30, 0.25)' : '0 1px 2px rgba(15,23,42,0.04)'};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  position: relative;
  @media (max-width: 640px) {
    flex: 0 0 min(78vw, 280px);
    scroll-snap-align: start;
  }
`;

const PopularBadge = styled.span`
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #fef2f2;
  color: #D52B1E;
  vertical-align: middle;
`;

const SelectPackageBtn = styled.button`
  appearance: none;
  margin-top: auto;
  width: 100%;
  box-sizing: border-box;
  padding: 0.7rem 0.75rem;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  background: ${(p) => (p.$selected ? '#D52B1E' : '#043663')};
  color: #fff;
  &:hover {
    opacity: 0.92;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

/* —— Fulfillment —— */
const ShippingFeeNote = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1.5px solid #043663;
  background: #eff6ff;
  font-size: 0.9rem;
  color: #0f172a;
  font-weight: 600;
`;

const ProductImage = styled.div`
  aspect-ratio: 4 / 3;
  background: #e8eef4;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ProductImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ProductBody = styled.div`
  padding: 1rem 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.65rem;
`;

const ProductName = styled.div`
  font-weight: 800;
  color: #0f172a;
  font-size: 1.02rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const FromPrice = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: #043663;
  letter-spacing: -0.02em;
`;

const DescWrap = styled.div`
  /* Typography comes from FormattedProductDescription (TinyMCE styles) */
  margin: 0.15rem 0 0.25rem;
  flex: 1;
  min-width: 0;
`;

const DescToggle = styled.button`
  margin-top: 0.55rem;
  padding: 0.2rem 0;
  border: none;
  background: none;
  color: #043663;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

const VariantGroupLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-top: 0.25rem;
`;

const VariantGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const VariantChip = styled.button`
  appearance: none;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  border: 1.5px solid ${(p) => (p.$selected ? '#043663' : '#d1d5db')};
  background: ${(p) => (p.$selected ? '#eff6ff' : '#fff')};
  color: #0f172a;
  padding: 0.45rem 0.55rem;
  min-width: 4.5rem;
  font-size: 0.78rem;
  line-height: 1.25;
  transition: border-color 0.12s ease, background 0.12s ease;
  &:hover {
    border-color: #043663;
  }
  &:focus-visible {
    outline: 2px solid #043663;
    outline-offset: 2px;
  }
`;

const ChipTitle = styled.div`
  font-weight: 650;
`;

const ChipPrice = styled.div`
  margin-top: 0.1rem;
  font-weight: 700;
  color: ${(p) => (p.$selected ? '#043663' : '#475569')};
  font-size: 0.75rem;
`;

const SelectedHint = styled.div`
  margin-top: 0.85rem;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  font-size: 0.8rem;
  font-weight: 600;
`;

/* —— Fulfillment —— */
const FulfillBlock = styled.div`
  margin-top: 1.1rem;
  padding-top: 1.1rem;
  border-top: 1px solid #e5e7eb;
`;

const MethodRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  margin-bottom: 1rem;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MethodCard = styled.button`
  appearance: none;
  cursor: pointer;
  text-align: left;
  padding: 0.85rem 0.9rem;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$selected ? '#043663' : '#d1d5db')};
  background: ${(p) => (p.$selected ? '#eff6ff' : '#fff')};
  transition: border-color 0.12s ease, background 0.12s ease;
  &:hover {
    border-color: #043663;
  }
`;

const MethodTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: #0f172a;
`;

const MethodSub = styled.div`
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: #64748b;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 0.65rem;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
  ${Input} {
    margin-bottom: 0;
  }
`;

const FieldFull = styled.div`
  grid-column: 1 / -1;
`;

const AddressCard = styled.div`
  margin-top: 0.35rem;
  padding: 0.75rem 0.85rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.4;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.75rem 0 0.5rem;
  font-size: 0.85rem;
  color: #374151;
  cursor: pointer;
  input {
    margin-top: 0.15rem;
  }
`;

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}

function variantChipLabel(v) {
  const parts = [v.sizeLabel, v.colorLabel].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  if (v.optionLabel && v.optionLabel !== 'Default') return v.optionLabel;
  return v.optionLabel || 'Standard';
}

function CollapsibleDescription({ text, t }) {
  const [open, setOpen] = useState(true);
  if (!text || !String(text).trim()) return null;
  const plainLen = productDescriptionPlainLength(text);
  const longCopy = plainLen > 220;
  const needsToggle = longCopy;
  return (
    <DescWrap>
      <FormattedProductDescription
        text={text}
        density="card"
        clamp={needsToggle && !open}
        lines={10}
      />
      {needsToggle ? (
        <DescToggle type="button" onClick={() => setOpen((o) => !o)}>
          {open
            ? t('registrationUpsell.showLess', 'Show less')
            : t('registrationUpsell.showMore', 'Show more')}
        </DescToggle>
      ) : null}
    </DescWrap>
  );
}

/**
 * Partner invite + upsell selection for player team registration.
 * @param {string} [sections='all'] - 'all' | 'partner' | 'packages' | 'fulfillment'
 */
export default function RegistrationPartnerAndUpsells({
  tenantId,
  seasonId,
  value,
  onChange,
  hidePartner = false,
  partnerMirrorHint: partnerMirrorHintProp = null,
  sections = 'all',
}) {
  const showPartner = sections === 'all' || sections === 'partner';
  const showPackages = sections === 'all' || sections === 'packages';
  const showFulfillment = sections === 'all' || sections === 'fulfillment';
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [deliveryFeeChf, setDeliveryFeeChf] = useState(7.5);
  const [profileDefaults, setProfileDefaults] = useState(null);
  const mirrorHint = value?.partnerMirrorHint || partnerMirrorHintProp || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await playerAPI.getRegistrationProducts(tenantId);
        const list = res?.data || [];
        if (!cancelled) {
          setProducts(Array.isArray(list) ? list : []);
          const fee = res?.deliveryFeeChf != null ? Number(res.deliveryFeeChf) : 7.5;
          setDeliveryFeeChf(fee);
          const catalogByVariantId = {};
          const catalogByProductId = {};
          (Array.isArray(list) ? list : []).forEach((p) => {
            catalogByProductId[p.id] = p;
            (p.variants || []).forEach((v) => {
              catalogByVariantId[v.id] = {
                unitPriceChf: Number(v.unitPriceChf) || 0,
                productId: p.id,
                productKind: p.productKind || 'addon',
                includesMerchandise: !!p.includesMerchandise,
                freeShipping: !!p.freeShipping,
              };
            });
          });
          onChange((prev) => {
            const base = prev && typeof prev === 'object' ? prev : {};
            let sels = Array.isArray(base.upsellSelections) ? [...base.upsellSelections] : [];
            const listArr = Array.isArray(list) ? list : [];
            const preferred =
              listArr.find(
                (p) =>
                  p.isDefault &&
                  (p.productKind === 'entry_pass' || p.productKind === 'pass_upgrade') &&
                  (p.variants || []).length
              ) ||
              listArr.find(
                (p) => p.productKind === 'entry_pass' && (p.variants || []).length
              );
            if (sels.length === 0 && preferred) {
              sels = [{ variantId: preferred.variants[0].id, quantity: 1 }];
            }
            return {
              ...base,
              deliveryFeeChf: fee,
              catalogByVariantId,
              catalogByProductId,
              upsellSelections: sels,
            };
          });
        }
      } catch {
        if (!cancelled) setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, seasonId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await playerAPI.getProfile();
        const p = res?.data || res;
        if (!cancelled && p) {
          setProfileDefaults({
            firstName: p.firstName || '',
            lastName: p.lastName || '',
            phone: p.phoneNumber || '',
            street: p.streetAddress || '',
            postalCode: p.postalCode || '',
            city: p.city || '',
            country: p.country || 'CH',
          });
        }
      } catch {
        /* optional prefill */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const partner = value?.partner || null;
  const selections = value?.upsellSelections || [];
  const fulfillment = value?.fulfillment || {
    method: 'delivery',
    useDifferentAddress: false,
    firstName: '',
    lastName: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'CH',
  };

  useEffect(() => {
    if (!profileDefaults || value?.fulfillmentPrefetched) return;
    onChange((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      const f = base.fulfillment || {};
      return {
        ...base,
        fulfillmentPrefetched: true,
        fulfillment: {
          method: f.method || 'delivery',
          useDifferentAddress: !!f.useDifferentAddress,
          firstName: f.firstName || profileDefaults.firstName,
          lastName: f.lastName || profileDefaults.lastName,
          phone: f.phone || profileDefaults.phone,
          street: f.street || profileDefaults.street,
          postalCode: f.postalCode || profileDefaults.postalCode,
          city: f.city || profileDefaults.city,
          country: f.country || profileDefaults.country || 'CH',
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDefaults]);

  const setPartner = (p) => onChange((prev) => ({ ...(prev || {}), partner: p }));
  const setSelections = (s) => onChange((prev) => ({ ...(prev || {}), upsellSelections: s }));
  const setFulfillment = (f) => onChange((prev) => ({ ...(prev || {}), fulfillment: f }));

  const clearSelection = () => {
    setPartner(null);
  };

  const toggleVariant = (variantId, product) => {
    const kind = product?.productKind || 'addon';
    if (kind === 'entry_pass' || kind === 'pass_upgrade') {
      setSelections([{ variantId, quantity: 1 }]);
    }
  };

  const applyMirrorPackage = () => {
    const ids = mirrorHint?.variantIds;
    if (!Array.isArray(ids) || ids.length === 0) return;
    setSelections(ids.map((variantId) => ({ variantId, quantity: 1 })));
  };

  const entryProducts = products.filter((p) => p.productKind === 'entry_pass');
  const upgradeProducts = products.filter((p) => p.productKind === 'pass_upgrade');

  const hasMerchandise = selections.some((s) => {
    const meta = value?.catalogByVariantId?.[s.variantId];
    return !!meta?.includesMerchandise;
  });

  const chargeDeliveryFee = selections.some((s) => {
    const meta = value?.catalogByVariantId?.[s.variantId];
    return !!meta?.includesMerchandise && !meta?.freeShipping;
  });

  const effectiveDeliveryFee = chargeDeliveryFee ? Number(deliveryFeeChf) || 0 : 0;

  const effectiveAddress = fulfillment.useDifferentAddress
    ? fulfillment
    : {
        ...fulfillment,
        street: profileDefaults?.street || fulfillment.street,
        postalCode: profileDefaults?.postalCode || fulfillment.postalCode,
        city: profileDefaults?.city || fulfillment.city,
        country: profileDefaults?.country || fulfillment.country || 'CH',
        firstName: fulfillment.firstName || profileDefaults?.firstName || '',
        lastName: fulfillment.lastName || profileDefaults?.lastName || '',
        phone: fulfillment.phone || profileDefaults?.phone || '',
      };

  const addressLocked = !fulfillment.useDifferentAddress;
  const profileAddressComplete =
    !!(effectiveAddress.street && effectiveAddress.postalCode && effectiveAddress.city);

  const deliveryAddressReady =
    !hasMerchandise ||
    !!(
      String(effectiveAddress.street || '').trim() &&
      String(effectiveAddress.postalCode || '').trim() &&
      String(effectiveAddress.city || '').trim()
    );
  const contactReady =
    !hasMerchandise ||
    (!!String(fulfillment.firstName || profileDefaults?.firstName || '').trim() &&
      !!String(fulfillment.lastName || profileDefaults?.lastName || '').trim() &&
      !!String(fulfillment.phone || profileDefaults?.phone || '').trim());
  const fulfillmentReady = deliveryAddressReady && contactReady;
  const fulfillmentBlockReason = !hasMerchandise
    ? null
    : !contactReady
      ? t(
          'registrationUpsell.fixContactFirst',
          'Please add first name, last name and phone above — then you can checkout.'
        )
      : !deliveryAddressReady
        ? t(
            'registrationUpsell.fixAddressFirst',
            'Home delivery needs street, postal code and city. Fill them in below — then you can checkout.'
          )
        : null;

  // Delivery only (pickup removed from registration UI)
  useEffect(() => {
    if (fulfillment.method === 'pickup') {
      setFulfillment({ ...fulfillment, method: 'delivery' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillment.method]);

  // Profile missing shipping address: unlock fields automatically for home delivery
  useEffect(() => {
    if (!hasMerchandise) return;
    if (fulfillment.useDifferentAddress) return;
    if (profileAddressComplete) return;
    if (!profileDefaults) return;
    setFulfillment({
      ...fulfillment,
      useDifferentAddress: true,
      method: 'delivery',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMerchandise, profileDefaults, profileAddressComplete]);

  // Expose readiness + effective shipping to parent (partner invoice / register page)
  useEffect(() => {
    onChange((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      if (
        base.fulfillmentReady === fulfillmentReady &&
        base.fulfillmentBlockReason === fulfillmentBlockReason &&
        Number(base.effectiveDeliveryFeeChf) === Number(effectiveDeliveryFee) &&
        base.chargeDeliveryFee === chargeDeliveryFee
      ) {
        return base;
      }
      return {
        ...base,
        fulfillmentReady,
        fulfillmentBlockReason,
        effectiveDeliveryFeeChf: effectiveDeliveryFee,
        chargeDeliveryFee,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentReady, fulfillmentBlockReason, effectiveDeliveryFee, chargeDeliveryFee]);

  const renderPlayerCard = (player, { confirmed, onPrimary, onDismiss }) => {
    const img = player.profilePictureUrl ? publicAssetUrl(player.profilePictureUrl) : '';
    const name = player.name || player.email || '—';
    return (
      <PartnerCard $confirmed={confirmed}>
        <Avatar>{img ? <img src={img} alt="" /> : initialsFromName(name)}</Avatar>
        <PartnerMeta>
          {confirmed ? (
            <ConfirmedBadge>{t('registrationUpsell.partnerAdded', 'Partner added')}</ConfirmedBadge>
          ) : null}
          <PartnerName>{name}</PartnerName>
          {player.createAccount ? (
            <PartnerSub>
              {t(
                'registrationUpsell.inviteToCreate',
                'No HPS account yet — they will be invited by email'
              )}
            </PartnerSub>
          ) : (
            <PartnerSub>
              {[player.homeCanton, player.homeClub].filter(Boolean).join(' · ') || player.email}
            </PartnerSub>
          )}
          {player.skillLevel != null && !player.createAccount ? (
            <SkillPill>
              {t('registrationUpsell.skillLabel', 'Skill {{skill}}', { skill: player.skillLevel })}
            </SkillPill>
          ) : null}
        </PartnerMeta>
        <CardActions>
          {onPrimary ? (
            <Button type="button" onClick={onPrimary}>
              {t('registrationUpsell.addPartner', 'Add partner')}
            </Button>
          ) : null}
          {onDismiss ? (
            <Button type="button" $variant="danger" onClick={onDismiss}>
              {confirmed
                ? t('common.remove', 'Remove')
                : t('registrationUpsell.dismissCandidate', 'Not this player')}
            </Button>
          ) : null}
        </CardActions>
      </PartnerCard>
    );
  };

  if (sections === 'none') {
    return null;
  }

  return (
    <>
      {showPartner && !hidePartner && (
        <Box>
          <Label>{t('registrationUpsell.partnerTitle', 'Invite your partner (optional)')}</Label>
          <Help>
            {t(
              'registrationUpsell.partnerHelpSearch',
              'Search by name or email to find an existing HPS player. If your teammate is not registered yet, invite them by email — they will receive the team invite when they create their account.'
            )}
          </Help>

          {!partner ? (
            <PlayerInviteSearch mode="registration" onAddPartner={setPartner} />
          ) : (
            renderPlayerCard(partner, {
              confirmed: true,
              onDismiss: clearSelection,
            })
          )}
        </Box>
      )}

      {showPackages && products.length > 0 && (
        <Box>
          <SectionHead>
            <SectionTitle>
              {t('registrationUpsell.packageTitle', 'HPS League Pass & packages')}
            </SectionTitle>
            <Help style={{ marginBottom: 0 }}>
              {t(
                'registrationUpsell.packageHelp',
                'Choose your League Pass or a Team / Pro pack. One package per player.'
              )}
            </Help>
          </SectionHead>

          {mirrorHint?.message ? (
            <Msg $ok style={{ marginBottom: '0.85rem' }}>
              {mirrorHint.message}{' '}
              <Button type="button" $variant="secondary" onClick={applyMirrorPackage}>
                {t('registrationUpsell.mirrorSame', 'Buy the same')}
              </Button>
            </Msg>
          ) : null}

          <ProductRail $cols={Math.min(3, entryProducts.length + upgradeProducts.length) || 1}>
            {[...entryProducts, ...upgradeProducts].map((p) => {
              const selectedVariant = (p.variants || []).find((v) =>
                selections.find((s) => s.variantId === v.id)
              );
              const productSelected = !!selectedVariant;
              const defaultVariant = (p.variants || [])[0];
              const selectVariant = selectedVariant || defaultVariant;
              return (
                <ProductTile key={p.id} $active={productSelected}>
                  <ProductImage>
                    {p.hasImage ? (
                      <img src={publicAssetUrl(p.imageUrl) || p.imageUrl} alt="" />
                    ) : (
                      <ProductImagePlaceholder>
                        {t('registrationUpsell.noImage', 'No photo')}
                      </ProductImagePlaceholder>
                    )}
                  </ProductImage>
                  <ProductBody>
                    <ProductName>
                      {p.name}
                      {p.isDefault ? (
                        <PopularBadge>
                          {t('registrationUpsell.mostPopularBadge', 'Most popular')}
                        </PopularBadge>
                      ) : null}
                    </ProductName>
                    <FromPrice>
                      CHF {Number(p.fromPriceChf ?? p.basePriceChf).toFixed(2)}
                    </FromPrice>
                    <CollapsibleDescription text={p.description} t={t} />
                    {(p.variants || []).length > 1 ? (
                      <>
                        <VariantGroupLabel>
                          {t('registrationUpsell.chooseVariant', 'Choose option')}
                        </VariantGroupLabel>
                        <VariantGrid>
                          {(p.variants || []).map((v) => {
                            const selected = !!selections.find((s) => s.variantId === v.id);
                            return (
                              <VariantChip
                                key={v.id}
                                type="button"
                                $selected={selected}
                                aria-pressed={selected}
                                onClick={() => toggleVariant(v.id, p)}
                              >
                                <ChipTitle>{variantChipLabel(v)}</ChipTitle>
                                <ChipPrice $selected={selected}>
                                  CHF {Number(v.unitPriceChf).toFixed(2)}
                                </ChipPrice>
                              </VariantChip>
                            );
                          })}
                        </VariantGrid>
                      </>
                    ) : null}
                    {selectVariant ? (
                      <SelectPackageBtn
                        type="button"
                        $selected={productSelected}
                        onClick={() => {
                          if (!productSelected) toggleVariant(selectVariant.id, p);
                        }}
                      >
                        {productSelected
                          ? t('registrationUpsell.selectedPackage', 'Selected')
                          : t('registrationUpsell.selectPackage', 'Select')}
                      </SelectPackageBtn>
                    ) : null}
                  </ProductBody>
                </ProductTile>
              );
            })}
          </ProductRail>

          {showFulfillment && hasMerchandise && renderFulfillmentBlock()}
        </Box>
      )}

      {showFulfillment && !showPackages && hasMerchandise && (
        <Box>{renderFulfillmentBlock()}</Box>
      )}
    </>
  );

  function renderFulfillmentBlock() {
    return (
            <FulfillBlock style={showPackages ? undefined : { marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <SectionTitle>
                {t('registrationUpsell.fulfillmentTitle', 'How should we get merch to you?')}
              </SectionTitle>
              <Help>
                {t(
                  'registrationUpsell.fulfillmentHelp',
                  'Details are filled from your profile. Switch to a different address only if needed.'
                )}
              </Help>

              {fulfillmentBlockReason ? (
                <Msg $warn style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                  {fulfillmentBlockReason}
                </Msg>
              ) : null}

              <Label>{t('registrationUpsell.contactDetails', 'Contact')}</Label>
              <FieldGrid>
                <Input
                  placeholder={t('registrationUpsell.firstName', 'First name')}
                  value={fulfillment.firstName}
                  onChange={(e) =>
                    setFulfillment({ ...fulfillment, method: 'delivery', firstName: e.target.value })
                  }
                  required
                />
                <Input
                  placeholder={t('registrationUpsell.lastName', 'Last name')}
                  value={fulfillment.lastName}
                  onChange={(e) =>
                    setFulfillment({ ...fulfillment, method: 'delivery', lastName: e.target.value })
                  }
                  required
                />
                <FieldFull>
                  <Input
                    placeholder={t('registrationUpsell.phone', 'Phone')}
                    value={fulfillment.phone}
                    onChange={(e) =>
                      setFulfillment({ ...fulfillment, method: 'delivery', phone: e.target.value })
                    }
                    required
                  />
                </FieldFull>
              </FieldGrid>

              <CheckRow>
                <input
                  type="checkbox"
                  checked={!!fulfillment.useDifferentAddress}
                  onChange={(e) =>
                    setFulfillment({
                      ...fulfillment,
                      method: 'delivery',
                      useDifferentAddress: e.target.checked,
                      ...(e.target.checked
                        ? {}
                        : {
                            street: profileDefaults?.street || '',
                            postalCode: profileDefaults?.postalCode || '',
                            city: profileDefaults?.city || '',
                            country: profileDefaults?.country || 'CH',
                          }),
                    })
                  }
                />
                <span>
                  {t(
                    'registrationUpsell.differentAddress',
                    'Deliver to a different address'
                  )}
                </span>
              </CheckRow>

              {addressLocked && profileAddressComplete ? (
                <AddressCard>
                  <strong style={{ display: 'block', marginBottom: 4, fontSize: '0.75rem', color: '#64748b' }}>
                    {t('registrationUpsell.shippingTo', 'Shipping to')}
                  </strong>
                  {[
                    effectiveAddress.street,
                    `${effectiveAddress.postalCode} ${effectiveAddress.city}`,
                    effectiveAddress.country || 'CH',
                  ].join(', ')}
                </AddressCard>
              ) : (
                <FieldGrid style={{ marginTop: '0.35rem' }}>
                  <FieldFull>
                    <Input
                      placeholder={t('registrationUpsell.street', 'Street address')}
                      value={fulfillment.street}
                      onChange={(e) =>
                        setFulfillment({
                          ...fulfillment,
                          method: 'delivery',
                          street: e.target.value,
                        })
                      }
                    />
                  </FieldFull>
                  <Input
                    placeholder={t('registrationUpsell.postalCode', 'Postal code')}
                    value={fulfillment.postalCode}
                    onChange={(e) =>
                      setFulfillment({
                        ...fulfillment,
                        method: 'delivery',
                        postalCode: e.target.value,
                      })
                    }
                  />
                  <Input
                    placeholder={t('registrationUpsell.city', 'City')}
                    value={fulfillment.city}
                    onChange={(e) =>
                      setFulfillment({ ...fulfillment, method: 'delivery', city: e.target.value })
                    }
                  />
                  <FieldFull>
                    <Input
                      placeholder={t('registrationUpsell.country', 'Country')}
                      value={fulfillment.country || 'CH'}
                      onChange={(e) =>
                        setFulfillment({
                          ...fulfillment,
                          method: 'delivery',
                          country: e.target.value,
                        })
                      }
                    />
                  </FieldFull>
                </FieldGrid>
              )}

              <ShippingFeeNote style={{ marginTop: '0.85rem' }}>
                {t('registrationUpsell.methodDeliveryShort', 'Home delivery')}
                {' · '}
                {chargeDeliveryFee
                  ? t('registrationUpsell.methodDeliveryFee', '+ CHF {{fee}}', {
                      fee: Number(effectiveDeliveryFee).toFixed(2),
                    })
                  : t('registrationUpsell.methodDeliveryFree', 'Free shipping')}
              </ShippingFeeNote>
            </FulfillBlock>
    );
  }
}
